import React, { useState, useMemo, useEffect } from 'react';
import Select from 'react-select';
import ImageUploader from '../../components/ImageUploader';
import useUploadImages from '../../hooks/useUploadImages';
import useUpdatePet from '../../hooks/useUpdatePet';
import usePetsList from '../../hooks/usePetsList';

const showAlert = (type, message, method, timeout = 10000) => {
  const container = document.getElementById('contact_results_' + method);
  if (!container) return;

  container.innerHTML = `
    <br/>
    <div class="alert alert-${type}" role="alert">
      ${message}
    </div>
  `;

  setTimeout(() => {
    container.innerHTML = '';
  }, timeout);
};

const formatDateForInput = dateVal => {
  if (!dateVal) return '';
  if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateVal)) {
    return dateVal.slice(0, 10);
  }
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

const UpdateDog = () => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [currentImages, setCurrentImages] = useState([]);
  const [status, setStatus] = useState('slobodan');
  const [size, setSize] = useState('srednji');
  const [birthdate, setBirthdate] = useState('');
  const [about, setAbout] = useState('');
  const [aboutEn, setAboutEn] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [images, setImages] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [newSponsor, setNewSponsor] = useState({ name: '', sponsor_type: 'gold' });
  const [adminPassword, setAdminPassword] = useState('');
  const [resetKey, setResetKey] = useState(0);

  const { data: pets, isLoading: isPetsLoading } = usePetsList();
  const uploadImagesMutation = useUploadImages();
  const updatePetMutation = useUpdatePet();

  // Filter list to only dogs (and sort them by name)
  const dogs = useMemo(() => {
    if (!pets) return [];
    return pets
      .filter(p => p.pet_type === 'dog')
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [pets]);

  // Create option list for react-select
  const options = useMemo(() => {
    return dogs.map(dog => ({
      value: dog._id,
      label: `${dog.name} (${dog.status || 'available'})`,
      dog: dog,
    }));
  }, [dogs]);

  // Populate data when a dog is selected
  useEffect(() => {
    if (selectedOption) {
      setCurrentImages(selectedOption.dog.images || []);
      setSponsors(
        (selectedOption.dog.sponsors || []).map(sponsor => ({
          name: sponsor.name || '',
          sponsor_type: sponsor.sponsor_type || 'bronze',
        }))
      );
      const petStatus = selectedOption.dog.status;
      setStatus(
        petStatus === 'available' ? 'slobodan' :
          petStatus === 'reserved' ? 'rezervisan' :
            petStatus === 'fosterDK' ? 'fosterDK' :
              petStatus === 'fosterCZ' ? 'fosterCZ' :
              petStatus === 'adopted' ? 'usvojen' : 'slobodan'
      );
      const petSize = (selectedOption.dog.size_category || '').toLowerCase();
      setSize(
        petSize === 'small' || petSize === 'mali' ? 'mali' :
          petSize === 'large' || petSize === 'veliki' ? 'veliki' : 'srednji'
      );
      setBirthdate(formatDateForInput(selectedOption.dog.birth_date));
      setAbout(selectedOption.dog.about || '');
      setAboutEn(selectedOption.dog.about_en || '');
      setDescription(selectedOption.dog.description || '');
      setDescriptionEn(selectedOption.dog.description_en || '');
    } else {
      setCurrentImages([]);
      setSponsors([]);
      setStatus('slobodan');
      setSize('srednji');
      setBirthdate('');
      setAbout('');
      setAboutEn('');
      setDescription('');
      setDescriptionEn('');
    }
  }, [selectedOption]);

  const handleUpload = newFiles => {
    setImages(prev => {
      const merged = [...prev, ...newFiles];
      const unique = Array.from(
        new Map(merged.map(file => [file.name + file.size, file])).values()
      );
      return unique;
    });
  };

  const handleRemoveImage = removedFile => {
    setImages(prev => prev.filter(img => img !== removedFile));
  };

  const handleRemoveExistingImage = imgSrcToRemove => {
    setCurrentImages(prev => prev.filter(src => src !== imgSrcToRemove));
  };

  const handleAddSponsor = () => {
    const name = newSponsor.name.trim();
    if (!name) {
      showAlert('danger', 'Ime sponzora je obavezno!', 'update');
      return;
    }

    setSponsors(prev => [...prev, { name, sponsor_type: newSponsor.sponsor_type }]);
    setNewSponsor({ name: '', sponsor_type: 'gold' });
  };

  const handleUpdateSponsor = (index, field, value) => {
    setSponsors(prev =>
      prev.map((sponsor, sponsorIndex) =>
        sponsorIndex === index ? { ...sponsor, [field]: value } : sponsor
      )
    );
  };

  const handleRemoveSponsor = index => {
    setSponsors(prev => prev.filter((_, sponsorIndex) => sponsorIndex !== index));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!selectedOption) {
      showAlert('danger', 'Molimo odaberite psa!', 'update');
      return;
    }

    const pet = selectedOption.dog;
    const petStatusMapped =
      status === 'slobodan'
        ? 'available'
        : status === 'rezervisan'
          ? 'reserved'
          : status === 'fosterDK'
            ? 'fosterDK'
            : status === 'fosterCZ'
              ? 'fosterCZ'
            : 'adopted';

    const petSizeMapped =
      size === 'mali' ? 'small' : size === 'veliki' ? 'large' : 'medium';

    const originalBirthdate = formatDateForInput(pet.birth_date);
    const isBirthdateModified = birthdate !== originalBirthdate;
    const isStatusModified = petStatusMapped !== pet.status;

    const originalPetSize = (pet.size_category || '').toLowerCase();
    const originalPetSizeMapped =
      originalPetSize === 'small' || originalPetSize === 'mali'
        ? 'small'
        : originalPetSize === 'large' || originalPetSize === 'veliki'
          ? 'large'
          : 'medium';
    const isSizeModified = petSizeMapped !== originalPetSizeMapped;

    const isImagesModified =
      images.length > 0 || currentImages.length !== (pet.images || []).length;
    const originalSponsors = (pet.sponsors || []).map(sponsor => ({
      name: sponsor.name || '',
      sponsor_type: sponsor.sponsor_type || 'bronze',
    }));
    if (sponsors.some(sponsor => !sponsor.name.trim())) {
      showAlert('danger', 'Ime svakog sponzora je obavezno!', 'update');
      return;
    }
    const isSponsorsModified = JSON.stringify(sponsors) !== JSON.stringify(originalSponsors);

    const originalAbout = pet.about || '';
    const originalAboutEn = pet.about_en || '';
    const originalDescription = pet.description || '';
    const originalDescriptionEn = pet.description_en || '';

    const isAboutModified = about !== originalAbout;
    const isAboutEnModified = aboutEn !== originalAboutEn;
    const isDescriptionModified = description !== originalDescription;
    const isDescriptionEnModified = descriptionEn !== originalDescriptionEn;

    const isModified =
      isImagesModified ||
      isStatusModified ||
      isSizeModified ||
      isBirthdateModified ||
      isSponsorsModified ||
      isAboutModified ||
      isAboutEnModified ||
      isDescriptionModified ||
      isDescriptionEnModified;

    if (!isModified) {
      showAlert(
        'danger',
        'Nema izmjena na slikama, statusu, veličini, datumu rođenja, uvodu, opisu niti sponzorima!',
        'update'
      );
      return;
    }

    if (!size) {
      showAlert('danger', 'Veličina psa je obavezna!', 'update');
      return;
    }

    if (!birthdate) {
      showAlert('danger', 'Datum rođenja je obavezan!', 'update');
      return;
    }

    if (!about.trim()) {
      showAlert('danger', 'Kratki opis (uvod) je obavezan!', 'update');
      return;
    }

    if (!description.trim()) {
      showAlert('danger', 'Opis je obavezan!', 'update');
      return;
    }

    if (!adminPassword) {
      showAlert('danger', 'Admin lozinka je obavezna!', 'update');
      return;
    }

    const folder = `adoption/${pet.name}-${pet._id}`;

    try {
      // 1. Upload images (only if there are new ones)
      let uploadedImages = { urls: [] };
      if (images.length > 0) {
        try {
          uploadedImages = await uploadImagesMutation.mutateAsync({
            files: images,
            folder,
          });
          showAlert('success', 'Nove slike uspješno unesene!', 'upload');
        } catch (uploadErr) {
          console.error(uploadErr);
          showAlert('danger', 'Greška pri uploadu slika!', 'upload');
          return;
        }
      }

      // 2. Merge remaining existing images and new images
      const mergedImages = [...currentImages, ...uploadedImages.urls];
      // Determine the main image: keep original if still present, otherwise fallback to the first merged image
      let image_main = pet.image_main || '';
      if (!currentImages.includes(image_main)) {
        image_main = mergedImages[0] || '';
      }

      const updatedPayload = {
        image_main,
        images: mergedImages,
        status: petStatusMapped,
        size_category: petSizeMapped,
        birth_date: new Date(birthdate),
        about: about.trim(),
        about_en: aboutEn.trim(),
        description: description.trim(),
        description_en: descriptionEn.trim(),
        sponsors,
        adminPassword,
      };

      // 3. Update pet in DB
      try {
        await updatePetMutation.mutateAsync({
          id: pet._id,
          data: updatedPayload,
        });
        showAlert('success', 'Uspješno ažuriranje psa!', 'update');

        // Reset form
        setImages([]);
        setSize('srednji');
        setBirthdate('');
        setAbout('');
        setAboutEn('');
        setDescription('');
        setDescriptionEn('');
        setAdminPassword('');
        setResetKey(prev => prev + 1);
        setSponsors([]);
        setNewSponsor({ name: '', sponsor_type: 'gold' });
        setSelectedOption(null);
      } catch (updateErr) {
        console.error(updateErr);
        showAlert('danger', 'Greška pri ažuriranju psa (netačna lozinka)!', 'update');
      }

    } catch (err) {
      console.error(err);
      showAlert('danger', 'Došlo je do greške!', 'update');
    }
  };

  return (
    <div>
      <div className="col-lg-12">
        <div className="paws-house-bg1 bg-light-custom element-section">
          <div className="contact-info col-lg-8">
            <h4>Ažuriranje psa i slika</h4>

            <div id="contact_form">
              {isPetsLoading ? (
                <div>Učitavanje pasa...</div>
              ) : (
                <form className="form-group" onSubmit={handleSubmit}>
                  <div className="row">
                    {/* Dog selection */}
                    <div className="col-md-12 mb-3">
                      <label htmlFor="dogSelect">
                        Odaberi psa<span className="required">*</span>
                      </label>
                      <Select
                        options={options}
                        value={selectedOption}
                        onChange={setSelectedOption}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        placeholder="Pretraži i odaberi psa..."
                        isClearable
                        required
                      />
                    </div>

                    {/* Previews of existing images with removal option */}
                    {selectedOption && (
                      <div className="col-md-12 mb-4">
                        <label>Trenutne slike psa ({selectedOption.dog.name}) - klikni na &times; da ukloniš:</label>
                        {currentImages && currentImages.length > 0 ? (
                          <div className="row g-2 mt-1">
                            {currentImages.map((imgSrc, idx) => (
                              <div key={idx} className="col-4 col-md-3 col-lg-2" style={{ position: 'relative' }}>
                                <img
                                  src={imgSrc || '/img/dogimage404-funny.png'}
                                  alt={`current-${idx}`}
                                  className="img-thumbnail"
                                  style={{ height: '80px', width: '100%', objectFit: 'cover' }}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemoveExistingImage(imgSrc)}
                                  style={{
                                    position: 'absolute',
                                    top: 5,
                                    right: 15,
                                    background: 'rgba(220,53,69,0.9)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    lineHeight: '22px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    padding: 0
                                  }}
                                  title="Ukloni sliku"
                                >
                                  &times;
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-muted mt-1">Nema preostalih slika.</div>
                        )}
                      </div>
                    )}

                    {/* Status selection */}
                    <div className="col-md-4 mb-3">
                      <label htmlFor="statusSelect">
                        Status<span className="required">*</span>
                      </label>
                      <select
                        className="form-control"
                        id="statusSelect"
                        value={status}
                        onChange={e => setStatus(e.target.value)}
                        disabled={!selectedOption}
                        required
                      >
                        <option>slobodan</option>
                        <option>rezervisan</option>
                        <option>fosterDK</option>
                        <option>fosterCZ</option>
                        <option>usvojen</option>
                      </select>
                    </div>

                    {/* Size selection */}
                    <div className="col-md-4 mb-3">
                      <label htmlFor="sizeSelect">
                        Veličina<span className="required">*</span>
                      </label>
                      <select
                        className="form-control"
                        id="sizeSelect"
                        value={size}
                        onChange={e => setSize(e.target.value)}
                        disabled={!selectedOption}
                        required
                      >
                        <option>srednji</option>
                        <option>mali</option>
                        <option>veliki</option>
                      </select>
                    </div>

                    {/* Birthdate selection */}
                    <div className="col-md-4 mb-3">
                      <label htmlFor="birthdateSelect">
                        Datum rođenja<span className="required">*</span>
                      </label>
                      <input
                        type="date"
                        id="birthdateSelect"
                        name="birthdate"
                        className="form-control input-field"
                        value={birthdate}
                        onChange={e => setBirthdate(e.target.value)}
                        disabled={!selectedOption}
                        required
                      />
                    </div>

                    {/* Introduction (Kratki opis / Uvod) */}
                    <div className="col-md-12 mb-3">
                      <label htmlFor="aboutInput">
                        Kratki opis (Uvod)<span className="required">*</span>
                      </label>
                      <textarea
                        id="aboutInput"
                        name="about"
                        rows="2"
                        className="form-control textarea-field"
                        style={{ borderRadius: 0 }}
                        value={about}
                        onChange={e => setAbout(e.target.value)}
                        disabled={!selectedOption}
                        required
                      />
                    </div>

                    {/* Introduction in English (Kratki opis na engleskom) */}
                    <div className="col-md-12 mb-3">
                      <label htmlFor="aboutEnInput">
                        Kratki opis na engleskom (Uvod EN)
                      </label>
                      <textarea
                        id="aboutEnInput"
                        name="about_en"
                        rows="2"
                        className="form-control textarea-field"
                        style={{ borderRadius: 0 }}
                        value={aboutEn}
                        onChange={e => setAboutEn(e.target.value)}
                        disabled={!selectedOption}
                      />
                    </div>

                    {/* Description (Opis) */}
                    <div className="col-md-12 mb-3">
                      <label htmlFor="descriptionInput">
                        Opis<span className="required">*</span>
                      </label>
                      <textarea
                        id="descriptionInput"
                        name="description"
                        rows="4"
                        className="form-control textarea-field"
                        style={{ borderRadius: 0 }}
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        disabled={!selectedOption}
                        required
                      />
                    </div>

                    {/* Description in English (Opis na engleskom) */}
                    <div className="col-md-12 mb-3">
                      <label htmlFor="descriptionEnInput">
                        Opis na engleskom
                      </label>
                      <textarea
                        id="descriptionEnInput"
                        name="description_en"
                        rows="4"
                        className="form-control textarea-field"
                        style={{ borderRadius: 0 }}
                        value={descriptionEn}
                        onChange={e => setDescriptionEn(e.target.value)}
                        disabled={!selectedOption}
                      />
                    </div>

                    {/* Sponsor management */}
                    {selectedOption && (
                      <div className="col-md-12 mb-4">
                        <label>Sponzori</label>
                        {sponsors.map((sponsor, index) => (
                          <div className="row g-2 mb-2" key={index}>
                            <div className="col-md-6">
                              <input
                                type="text"
                                className="form-control"
                                value={sponsor.name}
                                onChange={e => handleUpdateSponsor(index, 'name', e.target.value)}
                                placeholder="Ime sponzora"
                                aria-label={`Ime sponzora ${index + 1}`}
                              />
                            </div>
                            <div className="col-md-4">
                              <select
                                className="form-control"
                                value={sponsor.sponsor_type}
                                onChange={e => handleUpdateSponsor(index, 'sponsor_type', e.target.value)}
                                aria-label={`Tip sponzora ${index + 1}`}
                              >
                                <option value="gold">gold</option>
                                <option value="silver">silver</option>
                                <option value="bronze">bronze</option>
                              </select>
                            </div>
                            <div className="col-md-2">
                              <button
                                type="button"
                                className="btn btn-danger w-100"
                                onClick={() => handleRemoveSponsor(index)}
                                style={{ marginTop: 0 }}
                              >
                                Obriši
                              </button>
                            </div>
                          </div>
                        ))}
                        <div className="row g-2 mt-2">
                          <div className="col-md-6">
                            <input
                              type="text"
                              className="form-control"
                              value={newSponsor.name}
                              onChange={e => setNewSponsor(prev => ({ ...prev, name: e.target.value }))}
                              placeholder="Novo ime sponzora"
                              aria-label="Novo ime sponzora"
                            />
                          </div>
                          <div className="col-md-4">
                            <select
                              className="form-control"
                              value={newSponsor.sponsor_type}
                              onChange={e => setNewSponsor(prev => ({ ...prev, sponsor_type: e.target.value }))}
                              aria-label="Novi tip sponzora"
                            >
                              <option value="gold">gold</option>
                              <option value="silver">silver</option>
                              <option value="bronze">bronze</option>
                            </select>
                          </div>
                          <div className="col-md-2">
                            <button
                              type="button"
                              className="btn btn-primary w-100"
                              onClick={handleAddSponsor}
                              style={{ marginTop: 0 }}
                            >
                              Dodaj
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Image Uploader */}
                    <div className="col-md-12 mb-3">
                      <label>Učitaj nove slike (WebP/JPEG formati)</label>
                      <ImageUploader
                        onUpload={handleUpload}
                        onRemove={handleRemoveImage}
                        resetKey={resetKey}
                      />
                    </div>

                    {/* Hidden Username to satisfy Chrome Password Manager heuristics */}
                    <input
                      type="text"
                      name="username"
                      value="admin"
                      autoComplete="username"
                      style={{ display: 'none' }}
                      readOnly
                    />

                    {/* Admin Password */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="adminPassword">
                        Admin Password<span className="required">*</span>
                      </label>
                      <input
                        type="password"
                        id="adminPassword"
                        name="adminPassword"
                        className="form-control input-field"
                        value={adminPassword}
                        onChange={e => setAdminPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    id="submit_btn"
                    className="btn btn-primary"
                  >
                    Sačuvaj izmjene
                  </button>
                </form>
              )}
              <div id="contact_results_upload"></div>
              <div id="contact_results_update"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateDog;
